import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CloudUpload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api, errMsg } from "../../lib/api";
import { ActionButton } from "../common";
import { CoreSyncDialog } from "./CoreSyncDialog";

const PAGES = ["/assets", "/customers", "/drivers", "/locations", "/requesters", "/users", "/admin/tenants"];

export const SharedCoreSync = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const eligible = PAGES.includes(pathname) && ["admin", "shop_manager", "platform_admin"].includes(user?.role);
  const [config, setConfig] = useState(null);
  const [job, setJob] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!eligible) return;
    let active = true;
    api.get("/shared-core-sync").then(({ data }) => {
      if (active) { setConfig(data); setJob(data.last_operation || null); }
    }).catch(() => { if (active) setConfig(null); });
    return () => { active = false; };
  }, [eligible, user?.id]);
  useEffect(() => {
    if (!job?.operation_id || !["preparing", "applying"].includes(job.status)) return;
    let active = true;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get(`/shared-core-sync/operations/${job.operation_id}`);
        if (!active) return;
        setJob(data); setError("");
        if (data.status === "completed") toast.success("Shared Core synced to Live");
      } catch (failure) { if (active) setError(errMsg(failure)); }
    }, 1500);
    return () => { active = false; clearInterval(timer); };
  }, [job?.operation_id, job?.status]);
  const run = async (path) => {
    setBusy(true); setError("");
    try { const { data } = await api.post(path); setJob(data); }
    catch (failure) { setError(errMsg(failure)); }
    finally { setBusy(false); }
  };
  if (!eligible || !config?.enabled) return null;
  return <>
    <section className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-amber-500/25 bg-amber-500/5 px-4 py-3" data-testid="shared-core-sync-banner">
      <div className="min-w-0"><p className="text-xs font-mono uppercase tracking-wide text-amber-500" data-testid="shared-core-sync-source">Development Shared Core</p><p className="mt-1 text-xs text-slate-400" data-testid="shared-core-sync-last-status">{["preparing", "applying"].includes(job?.status) ? job.step : job?.status === "completed" ? "Last sync completed. Review new changes whenever you’re ready." : "Publish Shared Core records to Live separately from application code."}</p></div>
      <ActionButton testId="sync-shared-core-trigger-button" variant="secondary" onClick={() => setOpen(true)} className="justify-center shrink-0"><CloudUpload size={16} /> Sync Shared Core to Live</ActionButton>
    </section>
    <CoreSyncDialog {...{ open, setOpen, job, busy, error }} tenantName={config.tenant_name} preview={() => run("/shared-core-sync/preview")} apply={() => run(`/shared-core-sync/operations/${job.operation_id}/apply`)} />
  </>;
};
